import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { FormInputs, schema } from "./constants";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { FormModeTypes } from "src/types/generic";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetLocationsList } from "src/api/locations";
import {
  useAddDriverMutation,
  useGetDriverById,
  useUpdateDriverMutation,
} from "src/api/delivery";

const AddEditDriverPage = () => {
  const { id } = useParams();
  const mode: FormModeTypes = !id ? "add" : "edit";
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const isComingFromActivePage = location.pathname.includes("active");

  const breadcrumbs = [
    <Link
      key="1"
      color={"#A1A5B7"}
      underline="hover"
      onClick={() => navigate(-1)}
      component="text"
      sx={{ cursor: "pointer" }}
    >
      {isComingFromActivePage ? "Active" : "Archived"} Drivers
    </Link>,

    <Typography key="2" color="#A1A5B7">
      {mode === "add" ? "Add New Driver" : "Edit Driver"}
    </Typography>,
  ];

  const methods = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    getValues,
  } = methods;

  // APIS
  const { data: locationsData, isFetching: isFetchingLocations  } = useGetLocationsList({ type: "active" });
  const { mutate, status, isPending, error } = useAddDriverMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateDriverMutation();
  const { data, isFetching } = useGetDriverById({
    id: id ?? "",
  });

  const driverData = data?.data;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { englishName, arabicName, email, nationalId, phone, location } =
      data;
    const dataToSend = {
      nameEn: englishName,
      nameAr: arabicName,
      email,
      phoneNumber: phone,
      nationalId: nationalId ?? "",
      locations: [location.id],
    };
    console.log({ data, dataToSend });
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({ id, ...dataToSend });
    }
  };

  //Reset form with driver data to edit
  useEffect(() => {
    if (driverData) {
      reset({
        englishName: driverData.nameEn,
        arabicName: driverData.nameEn,
        nationalId: driverData.nationalId,
        email: driverData.email,
        phone: driverData.phoneNumber,
        location: driverData.locations?.[0] ?? {},
      });
    }
  }, [driverData]);

  //Add driver success and error handling
  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The driver has been added successfully!",
        },
      ]);
      navigate("/delivery/active");
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.response?.data?.message || "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  //Update driver success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The Driver has been updated successfully!",
        },
      ]);
      navigate(-1);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.response?.data?.message ??
            "Oops! Something went wrong.",
        },
      ]);
    }
  }, [statusUpdate]);

  if (isFetching || isFetchingLocations) {
    return (
      <Box
        height={"80vh"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  console.log({ errors }, getValues(), error, errorUpdate);

  return (
    <Box p={{ xs: "15px", md: "30px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Typography fontSize={"22px"} mb={1} fontWeight={600}>
            {mode === "add" ? "Add New Driver" : "Edit Driver"}
          </Typography>
          {/* <RouterBreadcrumbs /> */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ color: "#5E6278" }}
          >
            {breadcrumbs}
          </Breadcrumbs>
          {/* Driver info */}
          <Box
            mt={3}
            p={{ xs: 2, md: 4 }}
            border={"1px solid #5B5B5B"}
            borderRadius={"10px"}
          >
            <Typography mb={3}>Driver info</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="englishName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Name in English"
                      {...field}
                      error={!!errors.englishName}
                      helperText={errors.englishName?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="arabicName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Name in Arabic"
                      {...field}
                      error={!!errors.arabicName}
                      helperText={errors.arabicName?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="nationalId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="National ID"
                      {...field}
                      error={!!errors.nationalId}
                      helperText={errors.nationalId?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="Email"
                      {...field}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      fullWidth
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                      disabled={mode === "edit"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Mobile Number"
                      {...field}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                {/* select location */}
                <FormControl fullWidth size="small" error={!!errors.location}>
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        size="small"
                        {...field}
                        options={locationsData?.locations ?? []}
                        getOptionLabel={(option) => option?.nameEn || ""}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.location}
                            helperText={errors.location?.message}
                            label={"Location"}
                          />
                        )}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={props.id}>
                            <Typography>{option.nameEn}</Typography>
                          </li>
                        )}
                        onChange={(_, data) => {
                          console.log({ data });
                          field.onChange(data);
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            mt={"50px"}
            gap={3}
          >
            <Button
              variant="outlined"
              sx={{ minWidth: { xs: "100px", md: "208px" } }}
              onClick={() =>
                navigate(
                  isComingFromActivePage
                    ? "/delivery/active"
                    : "/delivery/archived",
                )
              }
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ minWidth: { xs: "100px", md: "208px" } }}
              loading={isPending || isPendingUpdate}
            >
              Save
            </LoadingButton>
          </Stack>
        </FormProvider>
      </form>
    </Box>
  );
};

export default AddEditDriverPage;
